import { Injectable, inject } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { Store } from '@ngrx/store';
import { LDSingleKindContext } from 'launchdarkly-js-client-sdk';
import { of, switchMap, tap } from 'rxjs';

import { StripeSubscriptionStatuses } from '@ninety/platform/models/billing/stripe-subscription-statuses.enum';
import { LaunchDarklyService } from '@ninety/platform/services/services/launch-darkly.service';

import { selectCompany } from '../../app-global/company/company-state.selectors';
import { selectCurrentPerson } from '../../app-global/current-person/current-person.selectors';
import { appActions } from '../../app.actions';
import { selectCurrentUser } from '../users/users-state.selectors';

import { FeatureFlagActions } from './feature-flag-state.actions';
import { FeatureFlagFacade } from './feature-flag-state.facade';
import { FeatureFlagKeys } from './feature-flag-state.model';

@Injectable()
export class FeatureFlagStateEffects {
  private actions$ = inject(Actions);
  private facade = inject(FeatureFlagFacade);
  private launchDarklyService = inject(LaunchDarklyService);
  private store = inject(Store);

  initLaunchDarkly$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(FeatureFlagActions.initLaunchDarkly, appActions.bootstrap),
        tap(() => this.launchDarklyService.init({ kind: 'user', name: 'anonymous', key: 'anonymous', anonymous: true }))
      ),
    { dispatch: false }
  );

  closeLaunchDarklyConnection$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(FeatureFlagActions.closeLaunchDarklyConnection),
        tap(() => this.launchDarklyService.closeConnection())
      ),
    { dispatch: false }
  );

  /**
   * See packages/_shared/feature-flags/app-domain-objects-to-ld-user.logic.ts for BE user registration.
   *
   * Updates to the properties used to identify a user here must also take place on the BE.
   */
  updateLaunchDarklyUser$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(FeatureFlagActions.updateLaunchDarklyUser),
        concatLatestFrom(() => [
          // ToDo: Combine into a single selector when all 3 have been converted to ngrx
          this.store.select(selectCurrentUser),
          this.store.select(selectCurrentPerson),
          this.store.select(selectCompany),
        ]),
        tap(
          ([
            { currentUserOverride, personOverride, companyOverride },
            currentUserFromState,
            personFromState,
            companyFromState,
          ]) => {
            const user = currentUserOverride ?? currentUserFromState;
            const person = personOverride ?? personFromState;
            const company = companyOverride ?? companyFromState;

            const ldUser: LDSingleKindContext = {
              kind: 'user',
              key: user?._id,
              anonymous: false,
              email: person?.primaryEmail?.toLowerCase() ?? '',
              userRoleCode: user?.roleCode,
              userHasCreatedCompany: person?.hasCreatedCompany ?? false,
              userIsImplementer: person?.isImplementer ?? false,
              userPartnerType: person?.partnerType,

              companyId: company?._id,
              companyName: company?.name,
              companyImplementerCode: company?.implementerCode ?? '',
              companyImplementerFree: company?.implementerFree,
              companyAccountStatus: company?.accountStatus,
              companyBos: company?.bos ?? '',
              companyCreatedByPersonId: company?.createdByPersonId,
              companyIndustry: company?.industry ?? '',
              companyEmployeeRange: company?.employeeRange ?? '',
              //only using this since we just need to see if the company was coached (even though this field is depricated)
              companyPartnerKey: company?.partnerKey ?? '',

              companyTrialing: company?.subscription?.providerStatus === StripeSubscriptionStatuses.Trialing,

              personId: person?._id,
              guideEnabled: user?.guideEnabled ?? false,
            };
            this.launchDarklyService.updateContext(ldUser);
          }
        )
      ),
    { dispatch: false }
  );

  processFeatureFlagUpdate$ = createEffect(() =>
    this.actions$.pipe(
      ofType(FeatureFlagActions.processFeatureFlagUpdates),
      concatLatestFrom(() => [this.facade.featureFlags$, this.facade.selectUserFlagsAreLoaded$]),
      switchMap(([{ flags, fromLd }, currentFlags, userFlagsAreLoaded]) => {
        if (
          userFlagsAreLoaded && // Only test after the initial retrieval of the user flags
          currentFlags[FeatureFlagKeys.appRefresh] !== flags[FeatureFlagKeys.appRefresh] &&
          flags[FeatureFlagKeys.appRefresh] === true
        ) {
          return of(FeatureFlagActions.refreshApp());
        }
        return of(FeatureFlagActions.updatedFlagsProcessed({ flags, fromLd }));
      })
    )
  );

  refreshApp$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(FeatureFlagActions.refreshApp),
        tap(() => this.reloadPage())
      ),
    { dispatch: false }
  );

  reloadPage(): void {
    location.reload();
  }
}
